import {
  LocationFromWhereReviewWasStarted,
  ReviewTypes,
  SignUpAndOnboardingModalActivatedReason,
  SignUpAndOnboardingModalView,
  type UserData,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { cardStore, signUpAndOnboardingModalStore, useReviewDeckStore } from "../../zustand/globalStore";
import { trackEvent } from "./helper-methods";
import { shouldUseMiniReviewForDeck } from "./mini-review-eligibility";

/**
 * Opens the sign-up modal before an anonymous visitor reaches a review. The
 * folder remains mounted behind the modal until AppRouter resumes the requested
 * deck or question after authentication and onboarding. Closing the modal
 * without signing up drops the request (closeSignUpDialogWithoutSigningUp).
 */
export function openSignUpGateBeforeReview({
  user,
  reviewDestinationAfterAuthentication,
}: {
  user: Pick<UserData, "id"> | null | undefined;
  reviewDestinationAfterAuthentication: string;
}): boolean {
  if (user?.id) {
    return false;
  }

  useReviewDeckStore
    .getState()
    .setPendingReviewDestinationAfterAuthentication(reviewDestinationAfterAuthentication);
  signUpAndOnboardingModalStore
    .getState()
    .setShowModalSignUpAndLogin(
      SignUpAndOnboardingModalView.SignInOrSignUpOptionsView,
      SignUpAndOnboardingModalActivatedReason.UserWantsToStartStudyingCards
    );
  return true;
}

export function resumePendingReviewAfterAuthentication({
  user,
  navigate,
  trackReviewSessionStarted = trackEvent,
}: {
  user: UserData | null | undefined;
  navigate: (destination: string) => void;
  trackReviewSessionStarted?: typeof trackEvent;
}): string | null {
  const reviewDeckState = useReviewDeckStore.getState();
  const reviewDestination = reviewDeckState.pendingReviewDestinationAfterAuthentication;
  if (!user?.id || !reviewDestination) {
    return null;
  }

  // Clear first so an effect replay cannot emit a second start event or repeat
  // the navigation for the same authenticated review intent.
  reviewDeckState.setPendingReviewDestinationAfterAuthentication(null);

  const signUpModalState = signUpAndOnboardingModalStore.getState();
  if (signUpModalState.shouldShowModalSignUpAndLogin) {
    signUpModalState.setHideModalSignUpAndLogin();
  }

  const currentReviewDeck = reviewDeckState.currentReviewDeck;
  const currentCardState = cardStore.getState();
  const isSpecificQuestionDestination = /^\/review-deck\/[^/]+\/\d+$/.test(reviewDestination);
  const isWholeDeckDestination = /^\/review-deck\/[^/]+$/.test(reviewDestination);
  const numberOfCardsToReview = isWholeDeckDestination
    ? (currentCardState.allCardsForCurrentDeckAndSubdecks?.length ?? currentCardState.cardsData?.length ?? 0)
    : (currentCardState.cardsData?.length ?? currentCardState.allCardsForCurrentDeckAndSubdecks?.length ?? 0);
  if (
    currentCardState.isUserInMiniReviewMode &&
    !shouldUseMiniReviewForDeck({ deck: currentReviewDeck, user })
  ) {
    currentCardState.setIsUserInMiniReviewMode(false);
  }

  if (isWholeDeckDestination) {
    // The review route owns authenticated study ordering and last-answer
    // resume. Do not carry the anonymous folder's raw order into its first
    // render while that route fetches and prepares the authenticated cards.
    cardStore.setState({
      cardsData: null,
      currentCardData: null,
      indexOfCurrentCard: 0,
    });
  }

  trackReviewSessionStarted(
    "Started Review Session",
    {
      deckId: currentReviewDeck?.id,
      deckName: currentReviewDeck?.name,
      reviewType: ReviewTypes.DeckReview,
      locationFromWhereReviewWasStarted: LocationFromWhereReviewWasStarted.DeckOverview,
      buttonPressed: isSpecificQuestionDestination ? "Specific Question Selected" : "Start Studying",
      numOfCardsToReview: numberOfCardsToReview,
      deckOwnership: currentReviewDeck?.user_id === user.id ? "Own deck" : "Shared deck - new user",
      resumedAfterAuthentication: true,
      reviewDestination,
    },
    user,
    currentReviewDeck
  );
  navigate(reviewDestination);
  return reviewDestination;
}
